import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// This callback runs when the user returns from Emergent OAuth with
// `#session_id=<token>` appended to the URL. We exchange it silently for a
// first-party JWT cookie via /api/auth/google/session, then bounce to /dashboard.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const sessionId = params.get('session_id');

    const finish = async (redirectTo, state) => {
      // Strip the fragment so a reload doesn't re-run the exchange
      try {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      } catch (_) { /* noop */ }
      navigate(redirectTo, { replace: true, state });
    };

    if (!sessionId) {
      finish('/login');
      return;
    }

    (async () => {
      try {
        const resp = await axios.post(
          `${API}/auth/google/session`,
          {},
          { headers: { 'X-Session-ID': sessionId }, withCredentials: true }
        );
        // Refresh AuthContext user state from the newly-set cookie
        await refreshUser();
        const isNew = resp?.data?.is_new_user;

        // Detect "link Google" flow initiated from Settings — show a toast and
        // bounce back to the Settings > Sign-in Methods tab.
        let isLinking = false;
        try {
          isLinking = sessionStorage.getItem('dowurk_link_google') === '1';
          if (isLinking) sessionStorage.removeItem('dowurk_link_google');
        } catch (_) { /* noop */ }

        if (isLinking) {
          toast.success('Google account linked — you can sign in with either method.');
          finish('/account/settings', { user: resp?.data?.user });
          return;
        }

        finish(isNew ? '/pledge' : '/dashboard', { user: resp?.data?.user });
      } catch (_err) {
        finish('/login?google=failed');
      }
    })();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white" data-testid="auth-callback">
      <div className="text-center">
        <div className="w-10 h-10 border-4 border-[#006847] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-gray-600 text-sm">Signing you in with Google…</p>
      </div>
    </div>
  );
}
